import { useEffect, useState } from "react";
import { KYDScope } from "@common/interfaces/ICognito";
import { Badge, Box, Checkbox, HStack, Text, VStack } from "@chakra-ui/react";
import {
  organizationPermissionStructure,
  eventPermissionStructure,
  crmPermissionStructure,
  contactsPermissionStructure,
  PermissionNode,
} from "./PermissionStructures";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { Collapse, IconButton } from "@chakra-ui/react";

export const LABELS: Partial<
  Record<
    | KYDScope
    | "events"
    | "promo_codes"
    | "access_codes"
    | "ticket_types"
    | "marketing"
    | "metadata"
    | "attendees"
    | "waitlist"
    | "guestlist"
    | "analytics"
    | "organizations"
    | "bundles"
    | "users"
    | "bio"
    | "affiliates"
    | "boxoffice"
    | "payouts"
    | "overview"
    | "reports"
    | "crm"
    | "campaigns"
    | "contacts"
    | "segments"
    | "imports"
    | "external",
    string
  >
> = {
  "events/write": "Update all your event's information",
  "events/read": "View all of your event's information",
  "events/promo_codes:read": "View promocodes",
  "events/promo_codes:write": "Update promocodes",
  "events/access_codes:read": "View access codes",
  "events/access_codes:write": "Update access codes",
  "events/ticket_types:read": "View ticket types",
  "events/ticket_types:write": "Manage ticket types",
  "events/marketing:read": "View marketing",
  "events/marketing:write": "Update marketing",
  // "events/campaigns:read": "View campaigns",
  // "events/campaigns:write": "Update campaigns",
  "events/metadata:read": "View event metadata",
  "events/metadata:write": "Update event metadata",
  "events/guestlist:read": "View guest list",
  "events/guestlist:write": "Manage guest list",
  "events/attendees:read": "View attendees",
  "events/attendees:write": "Manage attendees",
  "events/waitlist:read": "View waitlist",
  "events/waitlist:write": "Manage waitlist and fulfill orders",
  "events/analytics:read": "View event analytics",
  "events/affiliates:read": "View event affiliates",
  "events/affiliates:write": "Manage event affiliates",
  "events/boxoffice:read": "View event data in box office app",
  "events/boxoffice:write":
    "Check in patrons and collect payments in box office app",
  "events/reports:read": "View event reports",
  "events/reports:write": "Manage who receives event reports",
  "organizations/read": "View all access to your organization",
  "organizations/write":
    "Change anything about your organization, including team, events, bundles, bio, etc.",
  "organizations/bundles:read": "View bundles",
  "organizations/bundles:write": "Manage bundles",
  "organizations/users:read": "View team",
  "organizations/users:write": "Manage team",
  "organizations/events:read": "View all events",
  "organizations/events:write": "Create new events",
  "organizations/bio:read": "View bio",
  "organizations/bio:write": "Manage bio",
  "organizations/payouts:read": "View payouts related data",
  "organizations/payouts:write": "Update payouts related data",
  "organizations/overview:read": "View organization overview",
  "organizations/overview:write": "Update organization overview",
  "crm/read": "View all engagement tools",
  "crm/write": "Manage all engagement tools",
  "crm/campaigns:read": "View campaigns",
  "crm/campaigns:write": "Manage campaigns",
  "crm/contacts:read": "View contacts",
  "crm/contacts:write": "Manage contacts",
  "crm/segments:read": "View segments",
  "crm/segments:write": "Manage segments",
  "crm/imports:read": "View contact imports",
  "crm/imports:write": "Manage contact imports",
  "external/contacts:write": "Write contacts to organization",
  promo_codes: "Promo Codes",
  ticket_types: "Ticket Types",
  marketing: "Marketing",
  metadata: "Metadata",
  guestlist: "Guestlist",
  events: "Events",
  access_codes: "Access Codes",
  attendees: "Attendees",
  analytics: "Analytics",
  affiliates: "Affiliates",
  organizations: "Organization",
  bundles: "Bundles",
  users: "Team",
  bio: "Bio",
  waitlist: "Waitlist",
  boxoffice: "Box Office",
  payouts: "Payouts",
  overview: "Overview",
  reports: "Reports",
  crm: "Engage",
  campaigns: "Campaigns",
  contacts: "Contacts",
  segments: "Segments",
  imports: "Imports",
  external: "External",
};

const getAllChildScopes = (node: PermissionNode): KYDScope[] => {
  let scopes: KYDScope[] = [node.scopes.read, node.scopes.write].filter(
    Boolean
  );
  if (node.children) {
    node.children.forEach((child) => {
      scopes = scopes.concat(getAllChildScopes(child));
    });
  }
  return scopes;
};

const PermissionCheckbox = ({
  node,
  isDisabled = false,
  selectedScopes,
  onToggle,
  level,
  parentReadChecked = false,
  parentWriteChecked = false,
  hideLabel = false,
}: {
  node: PermissionNode;
  isDisabled?: boolean;
  selectedScopes: Set<KYDScope>;
  onToggle: (scopes: KYDScope[], isChecked: boolean) => void;
  level: number;
  parentReadChecked?: boolean;
  parentWriteChecked?: boolean;
  hideLabel?: boolean;
}) => {
  const isReadChecked =
    selectedScopes.has(node.scopes.read) || parentReadChecked;

  const isWriteChecked =
    selectedScopes.has(node.scopes.write) || parentWriteChecked;

  const handleReadToggle = () => {
    const scopesToToggle = [
      node.scopes.read,
      ...getAllChildScopes(node).filter((scope) => scope.endsWith(":read")),
    ].filter(Boolean) as KYDScope[];
    onToggle(scopesToToggle, !isReadChecked);
  };

  const handleWriteToggle = () => {
    const scopesToToggle = [
      node.scopes.read,
      node.scopes.write,
      ...getAllChildScopes(node).filter((scope) => scope.endsWith(":write")),
    ].filter(Boolean) as KYDScope[];
    onToggle(scopesToToggle, !isWriteChecked);
  };

  return (
    <VStack
      my={2}
      w="full"
      borderWidth={level === 0 ? 1 : 0}
      borderColor={"gray.200"}
      p={2}
      spacing={1}
      borderRadius={8}
    >
      {!hideLabel && (
        <Text w="full" fontWeight={"bold"}>
          {LABELS[node.name] || node.name}
        </Text>
      )}
      {node.scopes.read ? (
        <Checkbox
          w="full"
          defaultChecked={isReadChecked}
          isChecked={isReadChecked || node.name === "metadata"}
          isDisabled={
            parentReadChecked || isDisabled || node.name === "metadata"
          }
          disabled={parentReadChecked || isDisabled || node.name === "metadata"}
          onChange={handleReadToggle}
        >
          <VStack w="full" spacing={0}>
            <HStack w="full" justifyContent={"space-between"}>
              <Text>{LABELS[node.scopes.read]} </Text>
              <Badge
                justifyItems={"center"}
                maxW={"100px"}
                size={"xl"}
                colorScheme={"green"}
              >
                READ
              </Badge>
            </HStack>
            <Text w="full" fontSize={"xs"}></Text>
          </VStack>
        </Checkbox>
      ) : null}
      {node.scopes.write ? (
        <Checkbox
          w="full"
          defaultChecked={isWriteChecked}
          isChecked={isWriteChecked}
          onChange={handleWriteToggle}
          disabled={parentWriteChecked || isDisabled}
          isDisabled={parentWriteChecked || isDisabled}
        >
          <VStack w="full" spacing={0}>
            <HStack w="full" justifyContent={"space-between"}>
              <Text>{LABELS[node.scopes.write]} </Text>
              <Badge
                justifyItems={"center"}
                maxW={"100px"}
                size={"xl"}
                colorScheme={"red"}
              >
                WRITE
              </Badge>
            </HStack>
          </VStack>
        </Checkbox>
      ) : null}
      <Box pl={4} w="full">
        {node.children?.map((child, index) => (
          <PermissionCheckbox
            isDisabled={isDisabled}
            key={index}
            node={child}
            selectedScopes={selectedScopes}
            onToggle={onToggle}
            level={level + 1}
            parentReadChecked={isReadChecked}
            parentWriteChecked={isWriteChecked}
          />
        ))}
      </Box>
    </VStack>
  );
};

const PermissionSelector = ({
  mode,
  scopes,
  isDisabled = false,
  onChange = () => {},
  collaboratorType,
}: {
  mode: "team" | "organization";
  scopes: KYDScope[];
  isDisabled?: boolean;
  onChange: (values: { scopes: KYDScope[] }) => void;
  collaboratorType?: "business" | "personal";
}) => {
  const [selectedScopes, setSelectedScopes] = useState<Set<KYDScope>>(
    new Set(scopes)
  );
  // Collapsible state for each group
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({
    organization: true,
    events: true,
    crm: true,
    contacts: true,
  });

  // Dynamically construct GROUPS based on mode
  const GROUPS = [
    ...(mode === "team"
      ? [
          {
            key: "organization",
            label: "Organization",
            nodes: organizationPermissionStructure,
          },
          {
            key: "crm",
            label: "Engage",
            nodes: crmPermissionStructure,
          },
          {
            key: "events",
            label: "Events",
            nodes: eventPermissionStructure,
          },
        ]
      : [
          {
            key: "events",
            label: "Events",
            nodes: eventPermissionStructure,
            summary: "Share information for this event with the collaborator",
          },
          ...(collaboratorType === "personal"
            ? []
            : [
                {
                  key: "contacts",
                  label: "Contacts",
                  nodes: contactsPermissionStructure,
                  summary:
                    "Share contacts information for this event with the collaborator",
                },
              ]),
        ]),
  ];

  useEffect(() => {
    setSelectedScopes(new Set(scopes));
  }, [scopes]);

  const handleToggle = (scopes: KYDScope[], isChecked: boolean) => {
    setSelectedScopes((prevScopes) => {
      const newScopes = new Set(prevScopes);
      scopes.forEach((scope) => {
        if (isChecked) {
          newScopes.add(scope);
        } else {
          newScopes.delete(scope);
        }
      });
      onChange({ scopes: Array.from(newScopes) });
      return newScopes;
    });
  };

  const isNodeDisabled = (node: PermissionNode) => {
    if (node.name === "events" && mode === "team") {
      return !(
        selectedScopes.has("organizations/events:read") ||
        selectedScopes.has("organizations/events:write")
      );
    }
    return false;
  };

  const handleGroupToggle = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <VStack w="full" overflowY={"scroll"} h="500px" spacing={4} align="stretch">
      {mode === "organization" ? (
        <>
          <Text fontWeight={"medium"} w="full">
            Organization has access to:
          </Text>
          <Text w="full">
            By default an invited organization will be able to see a summary of
            your event including: name, description, timeline, venue,
            performers, genres.
          </Text>
        </>
      ) : (
        <>
          <Text fontWeight={"medium"} w="full">
            This member has access to:
          </Text>
        </>
      )}
      {GROUPS.map((group) => (
        <Box key={group.key} borderWidth={1} borderRadius={8} p={2}>
          <HStack
            as="button"
            w="full"
            spacing={2}
            onClick={() => handleGroupToggle(group.key)}
            cursor="pointer"
            _hover={{ bg: "gray.50" }}
            borderRadius={6}
            px={2}
            py={1}
            justifyContent="space-between"
          >
            <VStack
              w="full"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Text fontWeight="bold" fontSize="lg">
                {group.label}
              </Text>
              {group.summary && (
                <Text alignSelf="flex-start" fontSize="sm" color="gray.500">
                  {group.summary}
                </Text>
              )}
            </VStack>
            <ChevronDownIcon
              transition="transform 0.2s"
              transform={
                expanded[group.key] ? "rotate(0deg)" : "rotate(-90deg)"
              }
              boxSize={6}
            />
          </HStack>
          <Collapse in={expanded[group.key]} animateOpacity>
            <VStack w="full" align="stretch" spacing={1} mt={2}>
              {group.nodes.map((node, index) => (
                <PermissionCheckbox
                  isDisabled={isNodeDisabled(node)}
                  key={index}
                  node={node}
                  selectedScopes={selectedScopes}
                  onToggle={handleToggle}
                  level={0}
                  hideLabel={
                    index === 0 &&
                    (LABELS[node.name] || node.name) === group.label
                  }
                />
              ))}
            </VStack>
          </Collapse>
        </Box>
      ))}
    </VStack>
  );
};

export default PermissionSelector;
